/**
 * Analytics Hub — top-level category hub for /analytics.
 * Cross-references content/analytics/*.md
 *
 * Lists 8 analytics pages:
 *  /analytics/dashboards, /analytics-performance-metrics, /analytics/engagement,
 *  /analytics/comparative, /predictive-analytics, /analytics/sentiment,
 *  /analytics/conversion, /custom-reports
 */

export const analyticsHubMeta = {
  title: "Perception X2 Analytics | Real-Time Perception Intelligence",
  description:
    "Eight perception analytics surfaces: real-time dashboards, performance metrics, engagement, comparative, predictive, sentiment, conversion, and custom reports. 144-dimensional insight at machine speed.",
  canonical: "https://perception.ac/analytics",
  slug: "analytics",
};

export interface AnalyticsChild {
  code: string;
  title: string;
  description: string;
  href: string;
  icon: string;
  accent: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet" | "rose";
}

export const analyticsChildren: AnalyticsChild[] = [
  {
    code: "01",
    title: "Dashboards",
    description:
      "Real-time perception analytics: 144-dimensional heatmaps, sentiment timelines, competitive positioning, share-of-voice tracking.",
    href: "/analytics/dashboards",
    icon: "LayoutDashboard",
    accent: "crimson",
  },
  {
    code: "02",
    title: "Performance Metrics",
    description:
      "Penetration, authenticity, conversion, and resonance metrics tracked against engagement baselines with millisecond telemetry.",
    href: "/analytics-performance-metrics",
    icon: "Gauge",
    accent: "purple",
  },
  {
    code: "03",
    title: "Engagement Analytics",
    description:
      "Granular engagement telemetry: dwell time, scroll depth, social amplification, share velocity, and content-per-network propagation.",
    href: "/analytics/engagement",
    icon: "Activity",
    accent: "cyan",
  },
  {
    code: "04",
    title: "Comparative Analytics",
    description:
      "Side-by-side perception comparison against competitors, historical baselines, industry benchmarks, and counterfactual scenarios.",
    href: "/analytics/comparative",
    icon: "GitCompare",
    accent: "emerald",
  },
  {
    code: "05",
    title: "Predictive Analytics",
    description:
      "Forward-looking perception forecasting: cascade probability, narrative trajectory, competitive pre-emption, and risk scoring.",
    href: "/predictive-analytics",
    icon: "TrendingUp",
    accent: "amber",
  },
  {
    code: "06",
    title: "Sentiment Analysis",
    description:
      "144-dimensional emotional mapping: tone, ideology, identity markers, narrative preference — across 50+ platforms and 15+ languages.",
    href: "/analytics/sentiment",
    icon: "Brain",
    accent: "denim",
  },
  {
    code: "07",
    title: "Conversion Analytics",
    description:
      "Perception-to-action attribution. Tie sentiment shifts and resonance spikes to conversion events, retention, and revenue outcomes.",
    href: "/analytics/conversion",
    icon: "Target",
    accent: "violet",
  },
  {
    code: "08",
    title: "Custom Reports",
    description:
      "Bespoke reporting for board-level briefings, regulatory submissions, campaign retrospectives, and ad-hoc strategic analysis.",
    href: "/custom-reports",
    icon: "FileText",
    accent: "rose",
  },
];

export interface AnalyticsStat {
  value: string;
  label: string;
  to: number;
  suffix?: string;
  decimals?: number;
  icon: string;
  accent: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet";
}

export const analyticsStats: AnalyticsStat[] = [
  { value: "8", label: "Analytics Surfaces", to: 8, icon: "BarChart3", accent: "crimson" },
  { value: "144", label: "Sentiment Dimensions", to: 144, icon: "Activity", accent: "purple" },
  { value: "50+", label: "Platform Coverage", to: 50, suffix: "+", icon: "Globe", accent: "cyan" },
  { value: "15+", label: "Languages", to: 15, suffix: "+", icon: "Languages", accent: "emerald" },
  { value: "1s", label: "Real-Time Refresh", to: 1, suffix: "s", decimals: 0, icon: "Zap", accent: "amber" },
];

export interface AnalyticsFeature {
  title: string;
  description: string;
  icon: string;
  accent: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet" | "rose";
}

export const analyticsFeatures: AnalyticsFeature[] = [
  {
    title: "144-Dimensional Heatmaps",
    description: "Visualize perception across 144 emotional and cognitive dimensions — far beyond the 5–10 dimensions of conventional platforms.",
    icon: "Grid3x3",
    accent: "crimson",
  },
  {
    title: "Real-Time Refresh",
    description: "All dashboards refresh on 1-second telemetry. Sub-second event ingestion across 50+ platforms and 15+ languages.",
    icon: "Zap",
    accent: "purple",
  },
  {
    title: "Cascade Forecasting",
    description: "Forward-looking cascade probability modeling on incoming signals — pre-empt narrative crystallization before it occurs.",
    icon: "TrendingUp",
    accent: "cyan",
  },
  {
    title: "Cross-Platform Attribution",
    description: "Track perception shifts across platforms with cross-channel attribution. Tie a sentiment spike to its source platform within seconds.",
    icon: "Share2",
    accent: "emerald",
  },
  {
    title: "Competitive Benchmarking",
    description: "Side-by-side perception comparison against named competitors, industry baselines, and your own historical performance.",
    icon: "Swords",
    accent: "amber",
  },
  {
    title: "Perception-to-Action Tie-In",
    description: "Connect perception signals to downstream conversion, retention, and revenue events. Closed-loop attribution across the funnel.",
    icon: "Target",
    accent: "denim",
  },
  {
    title: "Custom KPI Builder",
    description: "Define bespoke metrics against the underlying perception data fabric. Surface the KPIs that match your strategic posture.",
    icon: "Sliders",
    accent: "violet",
  },
  {
    title: "Exportable Reports",
    description: "Board-ready PDF exports, scheduled email briefings, API-driven data feeds, and direct CRM/BI integration.",
    icon: "FileDown",
    accent: "rose",
  },
];

export interface AnalyticsUseCase {
  title: string;
  body: string;
  icon: string;
  accent: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet" | "rose";
}

export const analyticsUseCases: AnalyticsUseCase[] = [
  {
    title: "Real-Time Crisis Sensing",
    body: "Detect emerging narrative cascades before they crystallize. Trigger automated responses the moment a perception signal crosses critical thresholds.",
    icon: "Siren",
    accent: "crimson",
  },
  {
    title: "Board-Level Briefings",
    body: "Generate board-ready perception briefings with one click. Quarterly narratives, executive summaries, and trend analysis rendered as premium PDFs.",
    icon: "Briefcase",
    accent: "purple",
  },
  {
    title: "Competitive Pre-Emption",
    body: "Map competitor perception posture in real time. Identify narrative weak points and deploy counter-narratives before competitors can consolidate.",
    icon: "Swords",
    accent: "cyan",
  },
  {
    title: "Campaign Performance",
    body: "Measure campaign lift across all 144 perception dimensions. Tie spend to perception outcomes with closed-loop attribution.",
    icon: "Megaphone",
    accent: "emerald",
  },
  {
    title: "Regulatory Reporting",
    body: "Compliance-grade reporting for regulated industries — export perception telemetry as audit-ready evidence with full lineage.",
    icon: "FileCheck",
    accent: "amber",
  },
  {
    title: "Investor & Market Intelligence",
    body: "Perception telemetry as an investor signal. Track narrative trajectory that influences valuation, sentiment shifts ahead of earnings, and analyst consensus drift.",
    icon: "LineChart",
    accent: "denim",
  },
];

export interface AnalyticsWhy {
  title: string;
  body: string;
  metric?: string;
  accent: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet" | "rose";
}

export const analyticsWhy: AnalyticsWhy[] = [
  {
    title: "144-Dimensional Granularity",
    body: "14× deeper than the 5–10 dimensions used by conventional sentiment platforms. See the perception states your competitors can't model.",
    metric: "144D",
    accent: "crimson",
  },
  {
    title: "Sub-Second Telemetry",
    body: "1-second refresh across all dashboards. Sub-second event ingestion. The fastest perception telemetry in the industry.",
    metric: "<1s",
    accent: "purple",
  },
  {
    title: "50+ Platforms, 15+ Languages",
    body: "Continuous ingestion across the full digital surface — social, search, news, forums, reviews, video, podcasts, and emerging surfaces.",
    metric: "50+",
    accent: "cyan",
  },
  {
    title: "Predictive by Default",
    body: "Every dashboard includes forward-looking cascade probability. Don't just see the present — anticipate the trajectory.",
    metric: "✓",
    accent: "emerald",
  },
  {
    title: "Closed-Loop Attribution",
    body: "Tie perception signals to downstream conversion and revenue events. The only perception platform that closes the loop with outcomes.",
    metric: "✓",
    accent: "amber",
  },
  {
    title: "Audit-Grade Lineage",
    body: "Every metric traceable to its source signal. SOC 2 Type II and ISO 27001 compliant reporting for regulated industries.",
    metric: "✓",
    accent: "denim",
  },
];

export interface AnalyticsFaq {
  q: string;
  a: string;
}

export const analyticsFaq: AnalyticsFaq[] = [
  {
    q: "What are the eight Perception X2 analytics surfaces?",
    a: "The eight analytics surfaces are: Dashboards (real-time perception analytics), Performance Metrics (penetration, authenticity, conversion), Engagement Analytics (granular engagement telemetry), Comparative Analytics (competitor benchmarking), Predictive Analytics (cascade forecasting), Sentiment Analysis (144-dimensional emotional mapping), Conversion Analytics (perception-to-action attribution), and Custom Reports (bespoke board-level reporting).",
  },
  {
    q: "How fast do the analytics refresh?",
    a: "All dashboards refresh on 1-second telemetry. Sub-second event ingestion means that perception signals appear in the dashboards within a second of occurring across any monitored platform. For high-priority signals, the platform pushes immediate notifications to subscribed channels.",
  },
  {
    q: "What is 144-dimensional sentiment analysis?",
    a: "144-dimensional sentiment analysis maps audience emotional and cognitive states across 144 dimensions — including emotional tone, ideological alignment, identity markers, narrative preference, temporal sensitivity, and contextual framing. This is 14× deeper than the 5–10 dimensions tracked by conventional sentiment platforms.",
  },
  {
    q: "Can I build custom metrics on top of the data?",
    a: "Yes. Every dashboard includes a custom KPI builder that lets you define bespoke metrics against the underlying perception data fabric. Custom metrics can combine any of the 144 dimensions, weighted by your strategic priorities, and surfaced across all dashboards.",
  },
  {
    q: "Does the analytics platform support export and integration?",
    a: "Yes. Board-ready PDF exports, scheduled email briefings, CSV/Parquet data exports, and direct API access for downstream BI tools. Native integrations are available for Salesforce, HubSpot, Tableau, Looker, and bespoke enterprise systems.",
  },
  {
    q: "Can I track competitors in the analytics?",
    a: "Yes. Comparative Analytics lets you map perception posture against named competitors, industry baselines, and your own historical performance. All 144 dimensions are available for competitive benchmarking, with side-by-side time-series visualization.",
  },
  {
    q: "How is predictive analytics different from descriptive?",
    a: "Descriptive analytics tells you what happened. Predictive analytics tells you what will happen next. Perception X2 predictive analytics uses cascade probability modeling on incoming perception signals to forecast narrative trajectory, competitive pre-emption opportunities, and emerging risks before they crystallize.",
  },
  {
    q: "Is the analytics data exportable for compliance?",
    a: "Yes. Compliance-grade exports are available with full source lineage. Every metric is traceable back to its raw signal, with timestamp, source platform, and original content preserved. SOC 2 Type II and ISO 27001 compliant reporting for regulated industries.",
  },
  {
    q: "Does the analytics platform integrate with our CRM?",
    a: "Yes. Native integrations with Salesforce, HubSpot, Microsoft Dynamics, and other major CRM platforms. The integration enables closed-loop attribution — tying perception signals to customer outcomes and revenue events.",
  },
  {
    q: "What languages does the analytics support?",
    a: "Perception X2 analytics supports 15+ languages natively, including English, Mandarin, Spanish, French, German, Portuguese, Arabic, Hindi, Japanese, Korean, Russian, Italian, Dutch, Turkish, and Vietnamese. Additional languages available on request with 4–6 week deployment.",
  },
];

export const analyticsCta = {
  eyebrow: "Engage Perception Intelligence",
  title: "Eight surfaces. One perception data fabric.",
  description:
    "Every Perception X2 analytics surface reads from the same 144-dimensional data fabric — so dashboards, predictions, and reports always agree. Begin with the surface that matches your operational priority.",
  primary: { label: "Request Analytics Demo", href: "/demo" },
  secondary: { label: "View Dashboards", href: "/analytics/dashboards" },
  trustItems: [
    "8 analytics surfaces",
    "144 dimensions",
    "50+ platforms",
    "Sub-second telemetry",
    "Audit-grade lineage",
  ],
};

/* Sentiment trend data — bespoke visual section */
export interface SentimentTrendPoint {
  day: string;
  positive: number;
  neutral: number;
  negative: number;
  perception: number;
}

export const sentimentTrend: SentimentTrendPoint[] = [
  { day: "D-14", positive: 38, neutral: 41, negative: 21, perception: 42 },
  { day: "D-12", positive: 41, neutral: 39, negative: 20, perception: 45 },
  { day: "D-10", positive: 39, neutral: 42, negative: 19, perception: 44 },
  { day: "D-08", positive: 44, neutral: 38, negative: 18, perception: 49 },
  { day: "D-06", positive: 47, neutral: 36, negative: 17, perception: 53 },
  { day: "D-04", positive: 51, neutral: 34, negative: 15, perception: 58 },
  { day: "D-02", positive: 55, neutral: 32, negative: 13, perception: 63 },
  { day: "D-00", positive: 61, neutral: 29, negative: 10, perception: 71 },
  { day: "D+02", positive: 66, neutral: 26, negative: 8, perception: 78 },
  { day: "D+04", positive: 71, neutral: 22, negative: 7, perception: 84 },
  { day: "D+06", positive: 75, neutral: 20, negative: 5, perception: 89 },
  { day: "D+08", positive: 78, neutral: 18, negative: 4, perception: 92 },
  { day: "D+10", positive: 80, neutral: 17, negative: 3, perception: 95 },
  { day: "D+12", positive: 82, neutral: 16, negative: 2, perception: 97 },
];

/* Engagement breakdown by platform — bespoke visual */
export interface EngagementChannel {
  channel: string;
  volume: number;
  resonance: number;
  conversion: number;
  accent: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet" | "rose";
}

export const engagementChannels: EngagementChannel[] = [
  { channel: "Social — X / Twitter", volume: 92, resonance: 78, conversion: 14, accent: "crimson" },
  { channel: "Social — LinkedIn", volume: 84, resonance: 88, conversion: 22, accent: "denim" },
  { channel: "Social — Reddit", volume: 71, resonance: 81, conversion: 11, accent: "amber" },
  { channel: "Social — Instagram", volume: 88, resonance: 84, conversion: 16, accent: "rose" },
  { channel: "Social — TikTok", volume: 95, resonance: 76, conversion: 9, accent: "purple" },
  { channel: "Search — Google", volume: 100, resonance: 65, conversion: 31, accent: "emerald" },
  { channel: "News — Outlets", volume: 62, resonance: 91, conversion: 18, accent: "cyan" },
  { channel: "Forums — Niche", volume: 38, resonance: 94, conversion: 27, accent: "violet" },
];
